import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { buildWrapPricingInput, DEFAULT_DEFECT_MINUTES_PER_PANEL, deriveFinishKeyFromName, priceWrapLine, resolveDisassemblyMinutesByLevel } from '@wrapforce/shared';
import type {
  DisassemblyLevel, FinishKey, RemovalTier, ServiceTypeWithCatalog, WrapCoverageInput,
  WrapPricingInput, WrapCoverageSnapshot,
} from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import type { DraftLine } from '../NewQuotePage';
import { useVehicleCatalogTrimDetail, useWorkspaceVehicleById, useWrapPricingProfile } from './pricingApi';
import {
  VehiclePicker, vehicleSelectionFromTrimDetail, vehicleSelectionFromWorkspaceVehicleItem,
  type CustomerVehicleHint, type VehicleSelection,
} from './VehiclePicker';
import { DisassemblySelector } from './DisassemblySelector';
import { ConditionChecklist } from './ConditionChecklist';
import { approximatePanel, FOOTAGE_UNITS, MaterialPicker, PanelCoverageSelector } from './MaterialPicker';
import { useInventoryItem } from '@/features/inventory/api';
import { PricingBreakdownSidebar } from './PricingBreakdownSidebar';
import { FINISH_DEFS, MODIFIER_DEFS } from './engineDefaults';

const REMOVAL_MINUTES_BY_TIER = { light: 150, moderate: 360, heavy_baked: 720 };

export interface WrapLineOutput {
  serviceKind: 'wrap';
  vehicleCatalogTrimId: string | null;
  workspaceVehicleId: string | null;
  coverage: WrapCoverageSnapshot;
  disassemblyLevel: DisassemblyLevel;
  doorJambs: boolean;
  estimatedMinutes: number;
  filmFtX10: number;
  materialCostCents: number;
  laborCostCents: number;
  lineTotalCents: number;
  breakdown: Record<string, unknown>;
  profileVersionId: string;
  finishKey: FinishKey;
  vehicleLabel: string;
  materialId: string;
  materialInventoryItemId: string | null;
}

function coverageToSnapshot(coverage: WrapCoverageInput): WrapCoverageSnapshot['coverage'] {
  if (coverage.kind === 'partial_panels') {
    return { kind: 'partial_panels', panelKeys: coverage.panels.map((p) => p.panelKey) };
  }
  return coverage;
}

// Serialized in-progress builder state (see NewQuotePage's DraftServiceState):
// the raw fields the shop has entered but not yet added, persisted on the draft
// and restored open on reopen. VehicleSelection is a plain data object, so it
// round-trips as-is with no refetch.
export interface WrapDraftState {
  vehicle: VehicleSelection | null;
  disassemblyLevel: DisassemblyLevel;
  doorJambs: boolean;
  removalTier: RemovalTier;
  defectPanelCount: number;
  correctionFeeCents: number;
  coverage: WrapCoverageInput;
  materialId: string;
  materialItemId: string;
}

export function VehicleAwareWrapLineBuilder({
  workspaceId, service, onAddLine, initialState, onStateChange, customerVehicleHint = null, customerId = null, editingLine = null, onCancelEdit,
}: {
  workspaceId: string | null;
  service: ServiceTypeWithCatalog;
  onAddLine: (line: WrapLineOutput) => void;
  // Restore-on-mount for an in-progress (not-yet-added) builder, and a callback
  // that emits the builder's serialized state as it changes (add mode only), so
  // the parent can persist it on the draft. See WrapDraftState.
  initialState?: Record<string, unknown>;
  onStateChange?: (state: Record<string, unknown>) => void;
  customerVehicleHint?: CustomerVehicleHint | null;
  customerId?: string | null;
  editingLine?: DraftLine | null;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation('quotes');
  const { data: profile, isLoading: profileLoading } = useWrapPricingProfile(workspaceId);

  const editingCoverage = editingLine?.coverage?.serviceKind === 'wrap' ? editingLine.coverage : null;
  const restore = (editingLine ? undefined : initialState) as Partial<WrapDraftState> | undefined;

  const [vehicle, setVehicle] = useState<VehicleSelection | null>(restore?.vehicle ?? null);
  const [disassemblyLevel, setDisassemblyLevel] = useState<DisassemblyLevel>(editingLine?.disassemblyLevel ?? restore?.disassemblyLevel ?? 'L0');
  const [doorJambs, setDoorJambs] = useState(editingLine?.doorJambs ?? restore?.doorJambs ?? false);
  // Not restored on edit: these are live per-interaction acknowledgments, not
  // data persisted on the line (WrapCoverageSnapshot has no field for them) -
  // re-editing a non-L0 line asks the shop to re-confirm, same as adding fresh.
  const [clipBreakageAck, setClipBreakageAck] = useState(false);
  const [adhesionRisk, setAdhesionRisk] = useState(false);
  const [removalTier, setRemovalTier] = useState<RemovalTier>(editingCoverage?.removalTier ?? restore?.removalTier ?? 'none');
  const [defectPanelCount, setDefectPanelCount] = useState(editingCoverage?.defectPanelCount ?? restore?.defectPanelCount ?? 0);
  const [correctionFeeCents, setCorrectionFeeCents] = useState(editingCoverage?.correctionFeeCents ?? restore?.correctionFeeCents ?? 0);
  const [coverage, setCoverage] = useState<WrapCoverageInput>(restore?.coverage ?? { kind: 'full_wrap' });
  const [materialId, setMaterialId] = useState(() => editingLine?.materialId || restore?.materialId || service.materials.find((m) => m.isActive)?.id || '');
  const [materialItemId, setMaterialItemId] = useState(() => editingLine?.materialInventoryItemId ?? restore?.materialItemId ?? '');

  // The picked inventory item's PRICE/UNIT drives the film material cost:
  // when the item is footage-priced (ft/sqft), it replaces the profile's
  // flat costPerFtCents in the engine input. The server-side recompute
  // (pricing/resolve.ts) applies the identical override from the persisted
  // materialInventoryItemId, keeping the exact-total save check intact.
  const { data: materialItem } = useInventoryItem(workspaceId, materialItemId || undefined);
  const materialCostPerFtCents =
    materialItem && FOOTAGE_UNITS.has(materialItem.unit) ? materialItem.priceCentsPerUnit : null;

  // Finish is derived from the picked film's product name (wrap film names
  // carry their finish word), not asked separately - one less field, and the
  // mockup/pricing finish can never contradict the material.
  const finishKey: FinishKey = materialItem ? deriveFinishKeyFromName(materialItem.name) : 'gloss';

  // Editing an existing line only carries the vehicle's ids (vehicleCatalogTrimId/
  // workspaceVehicleId), not the full VehicleSelection (baseFtX10/baseMinutes/
  // l1-l3 disassembly minutes etc.) - re-fetch it once so the shop doesn't have
  // to re-pick the same vehicle from search, and (for partial-panel coverage,
  // which only stores panelKeys) recompute each panel's approx ft/minutes once
  // the vehicle's own base values are known.
  const { data: trimDetail } = useVehicleCatalogTrimDetail(workspaceId, editingLine?.vehicleCatalogTrimId ?? undefined);
  const { data: workspaceVehicleDetail } = useWorkspaceVehicleById(workspaceId, editingLine?.workspaceVehicleId ?? undefined);
  const resolvingEditVehicle = !!(editingLine?.vehicleCatalogTrimId || editingLine?.workspaceVehicleId) && !vehicle;

  useEffect(() => {
    if (vehicle) return;
    const resolved = trimDetail
      ? vehicleSelectionFromTrimDetail(trimDetail)
      : workspaceVehicleDetail
        ? vehicleSelectionFromWorkspaceVehicleItem(workspaceVehicleDetail)
        : null;
    if (!resolved) return;
    setVehicle(resolved);
    if (editingCoverage?.coverage.kind === 'partial_panels') {
      setCoverage({
        kind: 'partial_panels',
        panels: editingCoverage.coverage.panelKeys.map((panelKey) => approximatePanel(panelKey, resolved.baseFtX10, resolved.baseMinutes)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDetail, workspaceVehicleDetail]);

  const baseInput: WrapPricingInput | null = useMemo(() => {
    if (!vehicle || !profile) return null;
    return buildWrapPricingInput({
      laborRateCentsPerHour: profile.resolved.laborRateCentsPerHour,
      designRateCentsPerHour: profile.resolved.designRateCentsPerHour,
      wasteBp: profile.resolved.wasteBp,
      markupBp: profile.resolved.markupBp,
      costPerFtCents: materialCostPerFtCents ?? profile.resolved.costPerFtCents,
      vehicleBaseFtX10: vehicle.baseFtX10,
      vehicleBaseMinutes: vehicle.baseMinutes,
      complexityBp: vehicle.complexityBp,
      coverage,
      modifiers: vehicle.modifiers,
      modifierDefs: MODIFIER_DEFS,
      finishKey,
      finishDefs: FINISH_DEFS,
      disassemblyLevel,
      disassemblyMinutesByLevel: resolveDisassemblyMinutesByLevel({
        vehicleL1Minutes: vehicle.l1DisassemblyMinutes,
        vehicleL2Minutes: vehicle.l2DisassemblyMinutes,
        vehicleL3Minutes: vehicle.l3DisassemblyMinutes,
      }),
      doorJambs,
      jambsFtX10: profile.resolved.doorJambsFtX10,
      jambsMinutes: profile.resolved.doorJambsMinutes,
      defectPanelCount,
      defectMinutesPerPanel: DEFAULT_DEFECT_MINUTES_PER_PANEL,
      correctionFeeCents,
      removalTier,
      removalMinutesByTier: REMOVAL_MINUTES_BY_TIER,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, profile, coverage, finishKey, disassemblyLevel, doorJambs, defectPanelCount, correctionFeeCents, removalTier, materialCostPerFtCents]);

  const result = baseInput ? priceWrapLine(baseInput) : null;

  const disassemblyAckOk = disassemblyLevel === 'L0' || clipBreakageAck;
  const hasEmptyPartialCoverage = coverage.kind === 'partial_panels' && coverage.panels.length === 0;
  // An itemId with its detail still loading must block Add: the price shown
  // (and saved) would be the profile fallback while the server would apply
  // the item's price, tripping the exact-total recompute check.
  const materialItemResolved = materialItemId === '' || !!materialItem;
  const canAdd = !!vehicle && !!profile && !!result && disassemblyAckOk && materialId !== '' && materialItemResolved && !hasEmptyPartialCoverage;

  // The single source of truth for this line's output - reused verbatim by the
  // explicit Add button and the continuous draft emit below, so both paths send
  // byte-identical fields (keeping the server's exact-total recompute check happy).
  const liveOutput: WrapLineOutput | null = canAdd && vehicle && profile && result
    ? {
        serviceKind: 'wrap',
        vehicleCatalogTrimId: vehicle.catalogTrimId,
        workspaceVehicleId: vehicle.workspaceVehicleId,
        coverage: {
          serviceKind: 'wrap',
          coverage: coverageToSnapshot(coverage),
          modifiers: vehicle.modifiers,
          finishKey,
          removalTier,
          defectPanelCount,
          correctionFeeCents,
          designMinutes: 0,
          printCostCents: 0,
          fixedAddOnCents: 0,
        },
        disassemblyLevel,
        doorJambs,
        estimatedMinutes: result.estimatedMinutes,
        filmFtX10: result.filmFtX10,
        materialCostCents: result.materialCostCents,
        laborCostCents: result.laborCostCents,
        lineTotalCents: result.lineSubtotalCents,
        breakdown: result.breakdown as unknown as Record<string, unknown>,
        profileVersionId: profile.versionId,
        finishKey,
        vehicleLabel: vehicle.label,
        materialId,
        materialInventoryItemId: materialItemId || null,
      }
    : null;

  // Persist the raw builder state (add mode only, and only once a vehicle is
  // picked) so leaving mid-configuration and reopening restores it. Deduped by
  // JSON so the parent only churns on real changes. Not the priced output - this
  // is the un-validated in-progress state, never sent through the line recompute.
  const draftState: WrapDraftState = { vehicle, disassemblyLevel, doorJambs, removalTier, defectPanelCount, correctionFeeCents, coverage, materialId, materialItemId };
  const draftStateKey = JSON.stringify(draftState);
  useEffect(() => {
    if (editingLine || !vehicle) return;
    onStateChange?.(draftState as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStateKey, editingLine]);

  function handleAddLine() {
    if (!liveOutput) return;
    onAddLine(liveOutput);
    toast.success(editingLine ? t('pricing.lineUpdatedToast') : t('pricing.wrap.toastAdded'));

    if (!editingLine) {
      // Reset for the next line.
      setVehicle(null);
      setDisassemblyLevel('L0');
      setDoorJambs(false);
      setClipBreakageAck(false);
      setAdhesionRisk(false);
      setRemovalTier('none');
      setDefectPanelCount(0);
      setCorrectionFeeCents(0);
      setCoverage({ kind: 'full_wrap' });
      setMaterialId(service.materials.find((m) => m.isActive)?.id ?? '');
      setMaterialItemId('');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.vehicle')}</h3>
        {resolvingEditVehicle ? (
          <p className="text-sm text-muted">{t('pricing.loadingVehicle')}</p>
        ) : (
          <VehiclePicker workspaceId={workspaceId} selection={vehicle} onSelect={setVehicle} customerVehicleHint={customerVehicleHint} customerId={customerId} />
        )}
      </section>

      {vehicle && baseInput ? (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.disassembly')}</h3>
            <DisassemblySelector
              workspaceId={workspaceId}
              vehicle={vehicle}
              baseInput={baseInput}
              disassemblyLevel={disassemblyLevel}
              onChangeLevel={setDisassemblyLevel}
              doorJambs={doorJambs}
              onChangeDoorJambs={setDoorJambs}
              clipBreakageAck={clipBreakageAck}
              onChangeClipBreakageAck={setClipBreakageAck}
              adhesionRisk={adhesionRisk}
              onChangeAdhesionRisk={setAdhesionRisk}
              onVehicleMinutesSaved={(l1, l2, l3) => setVehicle({ ...vehicle, l1DisassemblyMinutes: l1, l2DisassemblyMinutes: l2, l3DisassemblyMinutes: l3 })}
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.condition')}</h3>
            <ConditionChecklist
              removalTier={removalTier}
              onChangeRemovalTier={setRemovalTier}
              defectPanelCount={defectPanelCount}
              onChangeDefectPanelCount={setDefectPanelCount}
              correctionFeeCents={correctionFeeCents}
              onChangeCorrectionFeeCents={setCorrectionFeeCents}
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.panels')}</h3>
            <PanelCoverageSelector
              workspaceId={workspaceId}
              coverage={coverage}
              onChangeCoverage={setCoverage}
              vehicleBaseFtX10={vehicle.baseFtX10}
              vehicleBaseMinutes={vehicle.baseMinutes}
              vehicleRef={
                vehicle.workspaceVehicleId
                  ? { source: 'workspace', id: vehicle.workspaceVehicleId }
                  : vehicle.catalogVehicleId
                    ? { source: 'catalog', id: vehicle.catalogVehicleId }
                    : null
              }
            />
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.materialCoverage')}</h3>
            <MaterialPicker
              workspaceId={workspaceId}
              serviceTypeId={service.id}
              materials={service.materials}
              materialId={materialId}
              onChangeMaterialId={setMaterialId}
              itemId={materialItemId}
              onChangeItemId={setMaterialItemId}
              requiredFtX10={result?.filmFtX10 ?? null}
            />
          </section>

          {result ? <PricingBreakdownSidebar result={result} /> : null}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={!canAdd || profileLoading} onClick={handleAddLine}>
              {editingLine ? t('pricing.saveChanges') : t('pricing.addService')}
            </Button>
            {editingLine ? (
              <Button type="button" variant="secondary" onClick={onCancelEdit}>
                {t('pricing.cancelEdit')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t('pricing.pickVehicle')}</p>
      )}
    </div>
  );
}
